//* Import react and other packages
import { useEffect, useState } from "react";
//* Import react-bootstrap components
import { Button, Form, Spinner } from "react-bootstrap";

//* Import components
import { Attention } from "../../../component/base/Attention";
import { ErrorMessages } from "../../../component/common/ErrorMessages";
import { RestoreModal } from "../../../component/base/RestoreModal"; 

//* Import react-router hooks
import { useNavigate, useParams } from "react-router-dom";

//* Import icons
import { ArrowBack, SaveRounded } from "@material-ui/icons";

//* Import useTeachers hook
import { useTeachers } from "../../../hooks/teachers/useTeachers";

export function UsersTeacherForm({ editMode }: { editMode?: boolean }) {
  const navigate = useNavigate();
  const params = useParams();
  const token = localStorage.getItem("token") || "";

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    allow_select_all_questions: null as boolean | null,
    is_auditor: null as boolean | null,
  });
  const [resetPassword, setResetPassword] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [originalData, setOriginalData] = useState<any>(null);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [submitErrors, setSubmitErrors] = useState<string[]>([]);

  const [showRestoreModal, setShowRestoreModal] = useState(false);

  const {
    getTeacherById,
    createTeacherAsync,
    updateTeacher,
    isCreating,
    isUpdating,
    isDeleting,
    restoreByEmailTeacher,
  } = useTeachers(token);

  // Fetch teacher data in edit mode
  useEffect(() => {
    const fetchTeacherData = async () => {
      if (editMode && params.id) {
        setIsLoadingData(true);
        try {
          const teacherData = await getTeacherById(params.id);
          if (teacherData) {
            const newFormData = {
              name: teacherData.person.name || "",
              email: teacherData.person.email || "",
              allow_select_all_questions:
                teacherData.allow_select_all_questions !== undefined
                  ? teacherData.allow_select_all_questions
                  : null,
              is_auditor:
                teacherData.is_auditor !== undefined
                  ? teacherData.is_auditor
                  : null,
            };
            setFormData(newFormData);
            setOriginalData(teacherData);
          }
        } catch (error) {
          console.error("Error fetching teacher data:", error);
        } finally {
          setIsLoadingData(false);
        }
      }
    };

    fetchTeacherData();
  }, [editMode, params.id, getTeacherById]);

  // Real-time validation for the name field
  useEffect(() => {
    if (formData.name.length > 0 && formData.name.length < 3) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        name: "Nome deve ter no mínimo 3 letras.",
      }));
    } else {
      setErrors((prevErrors) => {
        const { name, ...rest } = prevErrors;
        return rest;
      });
    }
  }, [formData.name]);

  // Real-time validation for the email field
  useEffect(() => {
    const emailRegex = /\S+@\S+\.\S+/;
    if (formData.email.length > 0 && !emailRegex.test(formData.email)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        email: "Por favor, insira um email válido.",
      }));
    } else {
      setErrors((prevErrors) => {
        const { email, ...rest } = prevErrors;
        return rest;
      });
    }
  }, [formData.email]);

  const validateForm = () => {
    const errorsList: { [key: string]: string } = {};

    if (formData.name.length < 3) {
      errorsList.name = "Nome deve ter no mínimo 3 letras.";
    }

    const emailRegex = /\S+@\S+\.\S+/;
    if (!emailRegex.test(formData.email)) {
      errorsList.email = "Por favor, insira um email válido.";
    }

    if (formData.is_auditor === null) {
      errorsList.is_auditor =
        "Por favor, selecione se poderá atuar como auditor.";
    }

    if (formData.allow_select_all_questions === null) {
      errorsList.allow_select_all_questions =
        "Por favor, selecione se poderá selecionar todas as questões.";
    }

    return errorsList;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const errorsList = validateForm();

    if (Object.keys(errorsList).length > 0) {
      setErrors(errorsList);
      return;
    }

    try {
      if (editMode && params.id) {
        // Always include name in the update object
        const updateData: any = {
          name: formData.name,
          allow_select_all_questions: formData.allow_select_all_questions,
          is_auditor: formData.is_auditor,
        };

        // Add reset_password if active
        if (resetPassword) {
          updateData.reset_password = true;
        }

        await updateTeacher({
          id: params.id,
          teacherData: updateData,
        });
        navigate("/users-teacher");
      } else {
        // Create mode
        await createTeacherAsync({
          name: formData.name,
          email: formData.email,
          allow_select_all_questions:
            formData.allow_select_all_questions ?? false,
          is_auditor: formData.is_auditor ?? false,
        });
        navigate("/users-teacher");
      }
    } catch (err:any) {
        console.error("Operation failed:", err);

        // Verifica se o erro é um AxiosError
        if (err.status) {
          if (err.status === 409) {
            setSubmitErrors(["Email já está registrado."]);
          } else if (err.status === 410) {
            setShowRestoreModal(true);
          } else {
            setSubmitErrors([
              "Ocorreu um erro ao salvar os dados. Por favor, tente novamente.",
            ]);
          }
        } else {
          setSubmitErrors([
            "Ocorreu um erro ao salvar os dados. Por favor, tente novamente.",
          ]);
        }
      }
    };
  
    const handleRestoreEmail = async () => {
        if (formData.email) {
          try {
            // Supondo que a API permita restaurar pelo email
            await restoreByEmailTeacher(formData.email);
            setShowRestoreModal(false);
            navigate("/users-teacher");
          } catch (err) {
            console.error("Erro ao restaurar o email:", err);
            setSubmitErrors([
              "Ocorreu um erro ao restaurar o email. Por favor, tente novamente.",
            ]);
          }
        }
    };
  

  const isLoading = isCreating || isUpdating || isLoadingData || isDeleting;

  return (
    <div className="row">
      <div className="col-12">
        <div className="py-5 px-3">
          <div className="card-body" id="card">
            <h4 className="mt-0 header-title">
              {editMode ? "Editar" : "Cadastrar"} Professor
            </h4>
            <p>
              {editMode
                ? "Edite um professor do seu sistema"
                : "Cadastre um professor do seu sistema"}
            </p>
            <br />

            {submitErrors.length > 0 && (
              <ErrorMessages
                messages={submitErrors}
                clearErrors={() => setSubmitErrors([])}
              />
            )}

            {!isLoading && (
              <Form onSubmit={handleSubmit} noValidate>
                <div className="row">
                  <div className="col-lg">
                    <Form.Group className="mb-3">
                      <Form.Label htmlFor="nome">
                        Nome <Attention />
                      </Form.Label>
                      <Form.Control
                        id="nome"
                        placeholder="Nome"
                        value={formData.name}
                        onChange={(e) => {
                          const newName = e.target.value;
                          setFormData({ ...formData, name: newName });
                        }}
                        isInvalid={!!errors.name}
                        required
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.name}
                      </Form.Control.Feedback>
                    </Form.Group>

                    {/* Radio buttons under the Name field */}
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Poderá atuar como auditor? <Attention />
                      </Form.Label>
                      <br />
                      <Form.Text className="text-muted">
                        <strong>Auditor:</strong> Professor que pode avaliar
                        questões criadas por outros professores. Ex: Professor de
                        português que irá atuar conferindo erros de português.
                      </Form.Text>
                      <div>
                        <Form.Check
                          type="radio"
                          id="is-auditor-yes"
                          data-testid="is-auditor-yes"
                          name="isAuditor"
                          label="Sim"
                          value="true"
                          checked={formData.is_auditor === true}
                          onChange={() => {
                            setFormData({ ...formData, is_auditor: true });
                            setErrors((prevErrors) => {
                              const { is_auditor, ...rest } = prevErrors;
                              return rest;
                            });
                          }}
                          isInvalid={!!errors.is_auditor}
                        />
                        <Form.Check
                          type="radio"
                          id="is-auditor-no"
                          data-testid="is-auditor-no"
                          name="isAuditor"
                          label="Não"
                          value="false"
                          checked={formData.is_auditor === false}
                          onChange={() => {
                            setFormData({ ...formData, is_auditor: false });
                            setErrors((prevErrors) => {
                              const { is_auditor, ...rest } = prevErrors;
                              return rest;
                            });
                          }}
                          isInvalid={!!errors.is_auditor}
                        />
                        {errors.is_auditor && (
                          <Form.Control.Feedback
                            type="invalid"
                            className="d-block"
                          >
                            {errors.is_auditor}
                          </Form.Control.Feedback>
                        )}
                      </div>
                    </Form.Group>
                  </div>

                  <div className="col-lg">
                    <Form.Group className="mb-3">
                      <Form.Label htmlFor="email">
                        E-mail <Attention />
                      </Form.Label>
                      <Form.Control
                        id="email"
                        type="email"
                        placeholder="E-mail"
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        disabled={editMode}
                        isInvalid={!!errors.email}
                        required
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.email}
                      </Form.Control.Feedback>
                    </Form.Group>

                    {/* Radio buttons under the Email field */}
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Poderá selecionar todas questões do sistema ao criar
                        prova? <Attention />
                      </Form.Label>
                      <br />
                      <Form.Text className="text-muted">
                        <strong>Questões do sistema ao criar prova:</strong>{" "}
                        Acesso às questões criadas apenas por ele(a) ou por outros
                        professores?
                      </Form.Text>
                      <div>
                        <Form.Check
                          type="radio"
                          id="allow-select-all-yes"
                          data-testid="allow-select-all-yes"
                          name="allowSelectAllQuestions"
                          label="Sim"
                          value="true"
                          checked={formData.allow_select_all_questions === true}
                          onChange={() => {
                            setFormData({
                              ...formData,
                              allow_select_all_questions: true,
                            });
                            setErrors((prevErrors) => {
                              const { allow_select_all_questions, ...rest } =
                                prevErrors;
                              return rest;
                            });
                          }}
                          isInvalid={!!errors.allow_select_all_questions}
                        />
                        <Form.Check
                          type="radio"
                          id="allow-select-all-no"
                          data-testid="allow-select-all-no"
                          name="allowSelectAllQuestions"
                          label="Não"
                          value="false"
                          checked={formData.allow_select_all_questions === false}
                          onChange={() => {
                            setFormData({
                              ...formData,
                              allow_select_all_questions: false,
                            });
                            setErrors((prevErrors) => {
                              const { allow_select_all_questions, ...rest } =
                                prevErrors;
                              return rest;
                            });
                          }}
                          isInvalid={!!errors.allow_select_all_questions}
                        />
                        {errors.allow_select_all_questions && (
                          <Form.Control.Feedback
                            type="invalid"
                            className="d-block"
                          >
                            {errors.allow_select_all_questions}
                          </Form.Control.Feedback>
                        )}
                      </div>
                    </Form.Group>
                  </div>
                </div>

                {editMode && (
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      id="reset-password-switch"
                      label="Resetar senha"
                      checked={resetPassword}
                      onChange={(e) => setResetPassword(e.target.checked)}
                    />
                    <Form.Text className="text-muted">
                      Ative essa opção para enviar uma nova senha para o
                      professor.
                    </Form.Text>
                  </Form.Group>
                )}

                <div className="w-100 d-flex justify-content-between mt-3">
                  <Button
                    variant="secondary"
                    className="d-flex align-items-center"
                    onClick={() => navigate("/users-teacher")}
                    type="button"
                  >
                    <ArrowBack className="me-2" /> Voltar
                  </Button>
                  <Button
                    variant="success"
                    type="submit"
                    disabled={isLoading}
                    className="d-flex align-items-center"
                  >
                    <SaveRounded className="me-2" /> Salvar
                  </Button>
                </div>
              </Form>
            )}

            {isLoading && (
              <div className="d-flex justify-content-center align-items-center mt-3">
                <Spinner animation="border" variant="primary" role="status" />
              </div>
            )}

            {/* Modal de restauração para erro 410 */}
            <RestoreModal
              show={showRestoreModal}
              onHide={() => setShowRestoreModal(false)}
              entityName="Professor"
              onRestore={handleRestoreEmail}
            />
          </div>
        </div>
      </div>
    </div>
  );

}
